import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskCodeScannerBlockConfig } from '@front-platform/content-builder';
import { kioskCodeScannerBlockResolveConfig } from '@front-platform/content-builder';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  computed,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KeyboardListenerDirective } from '@core/directives/keyboard-listener.directive';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ModalsService } from '@core/services/modals/modals.service';
import { TranslateService } from '@ngx-translate/core';
import {
  ComoRegistrationModalComponent,
  ComoRegistrationModalConfig,
} from '@shared/modals/como/como-registration-modal/como-registration-modal.component';
import { RegistrationStep } from '@shared/modals/como/como-registration-modal/registration-step.enum';
import {
  KioskAlertModalComponent,
  KioskAlertModalModalConfig,
} from '@shared/modals/kiosk-alert-modal/kiosk-alert-modal.component';
import { combineLatest } from 'rxjs';
import { ComoService } from '@core/services/como/como.service';
import {
  ensureKioskScanCodeEncoding,
  isPhoneLikeIdentificationCode,
  normalizePhoneLikeScan,
} from './kiosk-code-scanner-encoding.util';
export interface KioskCodeScannerBlockRuntimeConfig {}

export type KioskCodeScannerBlockConfigInput = Partial<
  Omit<KioskCodeScannerBlockConfig, keyof KioskCodeScannerBlockRuntimeConfig> &
    KioskCodeScannerBlockRuntimeConfig
>;
export type KioskCodeScannerBlockResolvedConfig = Omit<
  KioskCodeScannerBlockConfig,
  keyof KioskCodeScannerBlockRuntimeConfig
> &
  Partial<KioskCodeScannerBlockRuntimeConfig>;
export interface KioskCodeScannerBlockContract extends ConfigComponentContract<
  KioskCodeScannerBlockConfigInput,
  KioskCodeScannerBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-code-scanner-block',
  imports: [KeyboardListenerDirective],
  template: `
    <input
      class="invisible"
      appKeyboardListener
      (textBuffered)="onCodeBuffered($event)"
      id="keyboardInput"
      type="text"
      [value]="typedText"
      readonly
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KioskCodeScannerBlockComponent implements OnInit, KioskCodeScannerBlockContract {
  readonly config = input<KioskCodeScannerBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskCodeScannerBlockConfig,
    KioskCodeScannerBlockRuntimeConfig
  >(kioskCodeScannerBlockResolveConfig, () => ({ config: this.config() }));
  private readonly comoService = inject(ComoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly modalsService: ModalsService<
    [ComoRegistrationModalConfig, KioskAlertModalModalConfig]
  > = inject(ModalsService);
  private readonly translateService = inject(TranslateService);

  public accentColor = computed(
    () => this.resolvedConfig()['accent_color'] as unknown as IContentBuilderFieldColor,
  );

  public redirectUrl = computed(() => this.resolvedConfig()['redirect_url'] as unknown as string);

  private isValidating: boolean = false;
  private isBlockingModalOpen: boolean = false;

  private registrationStep = RegistrationStep;
  public typedText = '';
  public errorMessage = '';

  ngOnInit() {
    combineLatest([
      this.modalsService.getData(KioskAlertModalComponent.handle),
      this.modalsService.getData(ComoRegistrationModalComponent.handle),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([alertModal, registrationModal]) => {
        this.isBlockingModalOpen = !!alertModal || !!registrationModal;
        if (!this.isBlockingModalOpen) {
          this.isValidating = false;
        }
      });
  }

  public onCodeBuffered(code: string) {
    if (this.isValidating || this.isBlockingModalOpen) return;

    this.typedText = code;
    this.onSubmit(code);
  }

  private onSubmit(code: string): void {
    if (this.isValidating || this.isBlockingModalOpen) return;

    this.isValidating = true;

    if (isPhoneLikeIdentificationCode(code)) {
      this.submitPhoneAsOtpFlow(normalizePhoneLikeScan(code));
      return;
    }

    const encodedCode = ensureKioskScanCodeEncoding(code);

    this.comoService.linkComoUser$({ phoneOrEmail: undefined, code: encodedCode }).subscribe({
      error: () => this.handleErrorResponse(),
      next: () => this.redirectToUrl(),
    });
  }

  private submitPhoneAsOtpFlow(phone: string): void {
    this.comoService.linkComoUser$({ phoneOrEmail: phone }).subscribe({
      error: () => this.handleErrorResponse(),
      next: response => {
        if (response.otp_sent) {
          this.openRegistrationModal(this.registrationStep.STEP_OTP, phone);
          return;
        }
        this.redirectToUrl();
      },
    });
  }

  private redirectToUrl(): void {
    this.openRegistrationModal(this.registrationStep.STEP_KIOSK_CONFIRMATION);
  }

  private openRegistrationModal(activeStep: RegistrationStep, phone?: string): void {
    const accentColorValue = this.accentColor();
    this.modalsService.open(ComoRegistrationModalComponent.handle, undefined, {
      goToURLOnClose: this.redirectUrl() ?? '',
      activeStep,
      ...(phone ? { phone } : {}),
      ...(accentColorValue
        ? {
            accentColor: {
              background: accentColorValue,
            },
          }
        : {}),
    });
  }

  private handleErrorResponse(): void {
    this.isValidating = false;
    this.isBlockingModalOpen = true;

    this.showErrorDialog();
  }

  private showErrorDialog(): void {
    this.modalsService.open(KioskAlertModalComponent.handle, undefined, {
      icon: 'icon-warning-sign',
      message: this.translateService.instant('authentication.loyalty.unable-to-authenticate'),
      ...(this.accentColor() ? { accentColor: this.accentColor() } : {}),
    });
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskCodeScannerBlockComponent, { 'keyAliases': {} });
